import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Plane, Loader2, Globe } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(userId, password);
    
    if (result.success) {
      toast.success(t('welcomeBack'));
      navigate('/dashboard');
    } else {
      toast.error(result.error || t('loginError'));
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#1E3A5F]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://aercaribe.com/wp-content/uploads/2024/04/Vuelos-Charter-1.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-[#1E3A5F]/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img 
              src="https://aercaribe.com/wp-content/uploads/2024/07/Logo-Aer-Cargo-blanco.webp" 
              alt="AerCaribe Cargo" 
              className="h-16 object-contain"
            />
          </div>
          <div className="space-y-4">
            <h1 className="font-barlow text-4xl font-bold uppercase tracking-tight">
              Management System App
            </h1>
            <p className="text-lg text-white/80 max-w-md">
              {language === 'es' 
                ? 'Sistema de gestión para la aplicación móvil de AerCaribe Cargo'
                : 'Management system for AerCaribe Cargo mobile application'}
            </p>
          </div>
          <div className="flex items-center gap-2 text-white/60 text-sm">
            <Plane className="h-4 w-4" />
            <span>AerCaribe Cargo © {new Date().getFullYear()}</span>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          {/* Language Toggle */}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-slate-600 hover:text-[#1E3A5F] gap-2"
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4" />
              {language === 'es' ? 'English' : 'Español'}
            </Button>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center bg-[#1E3A5F] rounded-xl p-4 mb-2">
            <img 
              src="https://aercaribe.com/wp-content/uploads/2024/07/Logo-Aer-Cargo-blanco.webp" 
              alt="AerCaribe Cargo" 
              className="h-20 object-contain"
            />
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="font-barlow text-2xl font-bold uppercase tracking-tight text-[#1E3A5F]">
                {t('login')}
              </CardTitle>
              <CardDescription className="text-slate-500">
                {t('enterCredentials')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="userId" className="text-[#1E3A5F] font-medium">
                    {t('userId')}
                  </Label>
                  <Input
                    id="userId"
                    type="text"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder={t('userId')}
                    required
                    className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                    data-testid="login-userid-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1E3A5F] font-medium">
                    {t('password')}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('password')}
                    required
                    className="border-slate-200 focus-visible:ring-[#1E3A5F]"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#8B1538] hover:bg-[#6D102B] text-white font-medium uppercase tracking-wide"
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('loading')}
                    </>
                  ) : (
                    t('loginButton')
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
